// #region Imports
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  createMRTColumnHelper,
  type MRT_PaginationState as PaginationState,
  type MRT_Row,
  type MRT_RowSelectionState as RowSelectionState,
  type MRT_SortingState as SortingState,
  type MRT_TableOptions,
} from "material-react-table";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import TemplateOwnershipFilter, {
  type TemplateOwnershipScope,
} from "_shared/_views/templates/template-ownership-filter";
// #endregion

// #region Styles
const TopToolbarActions = styled("div")(({ theme }) => ({
  alignItems: "stretch",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  minWidth: 0,
  width: "100%",
}));
const ToolbarActionContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  flexShrink: 0,
  justifyContent: "flex-end",
  width: "auto",
  [theme.breakpoints.down("md")]: {
    marginLeft: theme.spacing(1),
  },
  "@container (min-width: 600px)": {
    width: "auto",
  },
}));
const NameContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(1),
  display: "flex",
  maxWidth: "100%",
  minWidth: 0,
  width: "100%",
}));
const RowName = styled(Typography)(() => ({
  flex: "1 1 auto",
  fontSize: "1em",
  fontWeight: 500,
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
/**
 * Collapsed into a labelled summary when the surface is narrow, open and headerless when it is not.
 *
 * `compact` is a prop rather than a media query because the panel's surface is not always the
 * window: on a proposal tab it is the page's pane, which the comments and category panels take a
 * column out of. A 1280px display with comments open left this drawn full height and fully expanded
 * in a 608px pane while the viewport query still called the display a wide one (#4964).
 */
const TemplateAccordion = styled(Accordion, {
  shouldForwardProp: (prop) => prop !== "compact",
})<{ compact?: boolean }>(({ compact, theme }) => ({
  backgroundColor: "transparent",
  boxShadow: "none",
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minWidth: 0,
  width: "100%",
  "&::before": {
    display: "none",
  },
  "& .MuiAccordionSummary-root": {
    display: "none",
    minHeight: 0,
  },
  "& .MuiAccordionSummary-content": {
    margin: theme.spacing(1, 0),
  },
  "& .MuiAccordionDetails-root": {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
    padding: 0,
  },
  "& .MuiCollapse-root, & .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner, & .MuiAccordion-region": {
    display: "flex",
    flex: 1,
    flexDirection: "column",
    minHeight: 0,
  },
  ...(compact && {
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
    height: "auto",
    marginBottom: theme.spacing(1),
    overflow: "hidden",
    "& .MuiAccordionSummary-root": {
      display: "flex",
    },
    "& .MuiAccordionDetails-root": {
      flex: "0 0 auto",
      padding: theme.spacing(0, 1, 1),
    },
    "& .MuiCollapse-root, & .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner, & .MuiAccordion-region": {
      flex: "0 0 auto",
    },
  }),
}));
// #endregion

// #region Row model
/**
 * One line of a template panel.
 *
 * The panel is deliberately told about rows rather than about templates. Documents, team templates
 * and fee-schedule templates have nothing in common as domain objects — one is a file, one holds a
 * leader and members, one holds priced categories — and the three panels the owner asked to look
 * alike differ only in what a row is called, what it draws beside the name, and what selecting it
 * does. Everything else, from the ownership toggle down to the paginator, is the same panel.
 *
 * A row owns its own answer to being selected. That is what lets the panel stay ignorant of all
 * three domains: it never computes a selection, it reports a press and redraws from whatever the
 * caller says is true next.
 */
export type TemplateSelectorRow = {
  /** Stable within the panel and the same across renders — the expander and the checkbox key off it. */
  id: string;
  /** What the row is called. The sort and the accessible names of its controls read this. */
  name: string;
  /**
   * Drawn in the Name cell in place of the bare name, where a row has more to carry — a file's type
   * glyph and its processing state, a template's tag, the line saying who owns it.
   */
  content?: ReactNode;
  /** Whether the row's checkbox is ticked. The caller's own state is the only source of this. */
  selected: boolean;
  /**
   * Ticked through something else: a team template whose people are already on the team without the
   * template itself having been applied, a document a selected template contributed.
   */
  indeterminate?: boolean;
  /** Greys the row's checkbox out. `disabledReason` is the tooltip that says why. */
  disabled?: boolean;
  disabledReason?: string;
  /** The row's own controls, already built — `RowActions` from `_shared/_views/tables/row-actions`. */
  actions?: ReactNode;
  /** The rows an expander reveals: a team template's people, a fee-schedule template's categories. */
  children?: readonly TemplateSelectorRow[];
  /** Called with what this row's selection should become. */
  onSelectedChanged: (selected: boolean) => void;
};

export type TemplateSelectorTab = {
  /** Reported back through `onTabChanged`. */
  value: string;
  label: string;
};

/** One row's press, for a caller hearing about a whole press at once — see `onSelectionChanged`. */
export type TemplateSelectorSelectionChange = {
  /** The `id` of the `TemplateSelectorRow` that changed. */
  readonly id: string;
  readonly selected: boolean;
};
// #endregion

export type TemplateSelectorProps = {
  className?: string;
  /**
   * Whether the surface drawing this is too narrow to hold the panel open. Defaults to the
   * viewport's `md` breakpoint, which is the right question wherever the panel has the window to
   * itself; a proposal tab passes what its pane measured instead.
   */
  compact?: boolean;
  /**
   * What the panel lists, plural and lower case — "templates", "team templates". The empty state is
   * worded from it, so "No team templates" and "No matching team templates" follow from one phrase
   * rather than being invented per panel.
   */
  subject: string;
  /** What the collapsed summary is called while nothing is selected. */
  collapsedLabel: string;
  /** How many of the caller's own things are selected, for the collapsed summary. */
  selectedCount: number;
  rows: readonly TemplateSelectorRow[];
  /** Whose templates are being shown, and what to call them in the toggle's accessible names. */
  ownership: {
    label: string;
    groupLabel?: string;
    value: TemplateOwnershipScope;
    onChange: (value: TemplateOwnershipScope) => void;
  };
  /**
   * The strip above the rows. Left off where a panel has only one source to draw from: a team
   * template panel has no second list to offer, and a strip with one tab in it says nothing.
   */
  tabs?: readonly TemplateSelectorTab[];
  activeTab?: string;
  onTabChanged?: (value: string) => void;
  /**
   * The search box, controlled rather than left to the table: a panel backed by a paged query has to
   * search on the server and one backed by a list in hand searches in hand. The panel should not
   * have to know which, so it is told the rows it is given are already the right ones.
   */
  search: {
    value: string;
    onChange: (value: string) => void;
  };
  /**
   * The paginator. `rowCount` is the total the caller's query reports rather than `rows.length`, so
   * a panel showing the first of six pages says so.
   */
  pagination: {
    value: PaginationState;
    onChange: (value: PaginationState) => void;
    rowCount: number;
  };
  /**
   * Where the caller wants the sort to happen. A panel whose rows arrive a page at a time must sort
   * on the server — sorting the page in hand would order ten of sixty rows and call it the order —
   * so it passes this and does the sorting itself. A panel holding every row leaves it off and the
   * table sorts them.
   */
  sorting?: {
    value: SortingState;
    onChange: (value: SortingState) => void;
  };
  /** Where the panel's rows are in flight or have failed, so the empty area can say which. */
  status?: {
    isLoading?: boolean;
    isFetching?: boolean;
    isError?: boolean;
    /**
     * What went wrong, in the API's own words, to stand where the failure state's generic second
     * line would.
     *
     * Take it from the rejected query with `getErrorMessageFromUnknown`, which gives the server's
     * own prose where the response carried Problem Details and the app's standard support sentence
     * where it did not — so `undefined` means there was no rejection to read, and the generic line
     * stands.
     */
    errorMessage?: string;
    onRetry?: () => void;
  };
  /**
   * The panel's own action, already built by the caller — Upload Template on a document panel,
   * Create Template on a team or fee-schedule one. A caller with nothing to offer, or without the
   * permission to offer it, passes nothing and the slot collapses.
   */
  action?: ReactNode;
  /** Anything the panel must keep mounted without drawing — a hidden file input, say. */
  children?: ReactNode;
  /**
   * Every row that changed in one press, together, for a caller that accumulates.
   *
   * A row's own `onSelectedChanged` is told once per row, which a select-all press turns into N
   * calls in one tick. A caller that derives its next state from a value captured at render — which
   * is all of them — then reads the same pre-press value N times and keeps only the last, so a
   * select-all over three rows committed one (#5163). Supply this and the press arrives once, as
   * what changed; the caller folds the lot into one update. `useAddAllColleagues` takes the same
   * shape for the same reason.
   *
   * **Supplying this is what offers select-all at all.** Without it the header checkbox is not
   * drawn, because the per-row contract cannot carry a press that changes more than one row. Rows
   * still report individually through `onSelectedChanged` either way — a single press is a
   * one-element batch, and a caller that reads it as a toggle keeps working.
   */
  onSelectionChanged?: (changes: readonly TemplateSelectorSelectionChange[]) => void;
};

const columnHelper = createMRTColumnHelper<TemplateSelectorRow>();

const columns = [
  columnHelper.accessor((row: TemplateSelectorRow) => row.name, {
    header: "Name",
    id: "name",
    // The one column a template panel has, so there is nothing to hide, reorder or filter it
    // against — its menu would offer a sort the header already offers and nothing else.
    enableColumnActions: false,
    grow: 1,
    minSize: 0,
    size: 240,
    muiTableBodyCellProps: {
      sx: {
        minWidth: 0,
        overflow: "hidden",
      },
    },
    Cell: renderNameCell,
  }),
];

/**
 * The panel a tab picks its templates from.
 *
 * This is the shape the Conflicts and Policies tabs have had all along — an ownership toggle, a tab
 * strip, an upload action, a search box and a table of named rows with checkboxes — lifted out of
 * the document selector so the Vendor Team and Fee Schedule tabs can wear it too (#4955). The owner
 * asked for the Conflicts panel's look on all of them; three copies of it would have made that true
 * this week and false by the next change to any one of them, so there is one.
 *
 * It knows nothing about templates. It is handed rows, and a row says what it is called, whether it
 * is ticked, what it draws and what to do when it is pressed.
 */
export default function TemplateSelector(props: Readonly<TemplateSelectorProps>) {
  const {
    action,
    activeTab,
    children,
    className,
    collapsedLabel,
    onSelectionChanged,
    onTabChanged,
    ownership,
    pagination,
    rows,
    search,
    selectedCount,
    sorting,
    subject,
    tabs,
  } = props;
  const status = props.status;
  const theme = useTheme();
  const viewportIsMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const isCompact = props.compact ?? viewportIsMdDown;
  // Starts collapsed where it collapses at all, which is the fee-schedule panel's behaviour and the
  // reason it has one: the templates are a picker and the tab's own editor is the work. Where the
  // surface is wide enough to hold the panel open this is never read.
  const [panelOpen, setPanelOpen] = useState(false);
  const expanded = !isCompact || panelOpen;

  // Every row in the panel, parents and children alike, by id. A press reaches this component as an
  // id, and walking the tree once per press would be the same walk repeated once per row.
  const rowsById = useMemo(() => {
    const index = new Map<string, TemplateSelectorRow>();
    const visit = (row: TemplateSelectorRow) => {
      index.set(row.id, row);
      for (const child of row.children ?? []) visit(child);
    };
    for (const row of rows) visit(row);
    return index;
  }, [rows]);

  // The table's selection is derived, never held. MRT keeps a record of its own and the caller keeps
  // the truth; deriving one from the other on every render is what stops the two drifting apart —
  // which is the failure the two-way effect between them was written to paper over, complete with a
  // ref to skip the echo.
  const rowSelection = useMemo(() => {
    const selection: RowSelectionState = {};
    for (const [id, row] of rowsById) selection[id] = row.selected;
    return selection;
  }, [rowsById]);

  const hasChildren = useMemo(() => rows.some((row) => (row.children?.length ?? 0) > 0), [rows]);

  const handleRowSelectionChange = useCallback(
    (updater: RowSelectionState | ((previous: RowSelectionState) => RowSelectionState)) => {
      const previous: RowSelectionState = {};
      for (const [id, row] of rowsById) previous[id] = row.selected;
      const next = typeof updater === "function" ? updater(previous) : updater;
      // What changed, and never the whole record: MRT hands back every row, and a caller told that
      // a row it already had ticked is ticked would untick it if it reads a report as a toggle —
      // which the template panels do.
      const changes: TemplateSelectorSelectionChange[] = [];
      for (const [id, row] of rowsById) {
        const selected = next[id] ?? false;
        if (selected !== row.selected) changes.push({ id, selected });
      }
      if (changes.length === 0) return;
      // One press, one report, where the caller asked to hear it that way. A select-all is the press
      // that changes more than one row, and N per-row calls in one tick is what no caller holding
      // accumulating state can absorb (#5163).
      if (onSelectionChanged) {
        onSelectionChanged(changes);
        return;
      }
      for (const change of changes) {
        rowsById.get(change.id)?.onSelectedChanged(change.selected);
      }
    },
    [onSelectionChanged, rowsById],
  );

  const renderTopToolbarCustomActions = useCallback(
    () => (
      <TopToolbarActions>
        <TemplateOwnershipFilter
          groupLabel={ownership.groupLabel}
          label={ownership.label}
          value={ownership.value}
          onChange={(value) => {
            ownership.onChange(value);
            pagination.onChange({ ...pagination.value, pageIndex: 0 });
          }}
        />
        {tabs && tabs.length > 0 && (
          <Tabs
            indicatorColor="primary"
            textColor="primary"
            value={activeTab ?? tabs[0].value}
            variant="fullWidth"
            onChange={(_event, value: string) => onTabChanged?.(value)}
          >
            {tabs.map((tab) => (
              <Tab key={tab.value} label={tab.label} value={tab.value} />
            ))}
          </Tabs>
        )}
      </TopToolbarActions>
    ),
    [activeTab, onTabChanged, ownership, pagination, tabs],
  );

  // An empty fragment rather than `null` where a panel has no action. MRT reads this result with `??`,
  // so a nullish return falls straight back to its own Show/Hide-columns, Toggle-filters and
  // Toggle-full-screen buttons — which is how the active Vendor Team panel, the one panel with nothing
  // to offer here, came to draw four icon buttons the Conflicts panel it is meant to match has never
  // had (#4955). This is a one-column picker; there is nothing to hide, filter or magnify.
  const renderToolbarInternalActions = useCallback(
    () => (action ? <ToolbarActionContainer>{action}</ToolbarActionContainer> : <></>),
    [action],
  );

  const renderRowActions = useCallback(
    (params: { row: MRT_Row<TemplateSelectorRow> }) => params.row.original.actions ?? null,
    [],
  );

  const muiTableBodyRowProps = useCallback(
    (params: { row: MRT_Row<TemplateSelectorRow> }) => {
      const row = params.row.original;
      const background = row.selected
        ? theme.palette.primary.light
        : row.indeterminate
          ? CONTRIBUTED_ROW_BACKGROUND
          : "inherit";
      return {
        selected: row.selected,
        sx: {
          backgroundColor: background,
          color: row.selected ? "rgba(0, 0, 0, 0.5)" : "inherit",
          cursor: row.disabled ? "default" : "pointer",
        },
        onClick: () => {
          if (!row.disabled) row.onSelectedChanged(!row.selected);
        },
      };
    },
    [theme],
  );

  const muiSelectCheckboxProps = useCallback((params: { row: MRT_Row<TemplateSelectorRow> }) => {
    const row = params.row.original;
    return {
      checked: row.selected,
      indeterminate: !row.selected && Boolean(row.indeterminate),
      title: row.disabled ? row.disabledReason : undefined,
    };
  }, []);

  // Built inline rather than memoized. Half of what goes in here is an object the caller allocated
  // this render — `search`, `pagination`, `ownership` — so a memo keyed on them would rebuild every
  // time anyway, and `MRTNoMemo` is opted out of memoization in any case.
  const tableOptions: MRT_TableOptions<TemplateSelectorRow> = {
    columns,
    data: rows as TemplateSelectorRow[],
    getRowId: (row) => row.id,
    getSubRows: (row) => row.children as TemplateSelectorRow[] | undefined,
    enableExpanding: hasChildren,
    // A parent's press is the caller's to interpret — applying a team template is not the same act
    // as ticking each of its people — so MRT is told not to cascade one into the other.
    enableSubRowSelection: false,
    enableColumnFilterModes: false,
    enableColumnOrdering: false,
    enableColumnPinning: false,
    enableFacetedValues: true,
    enableGrouping: false,
    enableRowActions: true,
    enableRowSelection: (row) => !row.original.disabled,
    // Offered only to a caller that can hear a whole press at once. `enableRowSelection` being a
    // function is enough to draw the header checkbox, so leaving this to its default put a
    // select-all on all six panels that committed one row of N (#5163); the panels whose ticks mean
    // "apply this template" have no batch handler and no defined meaning for applying ten at once,
    // so they no longer offer it.
    enableSelectAll: Boolean(onSelectionChanged),
    enableSorting: true,
    enableStickyFooter: true,
    enableStickyHeader: true,
    enableTableHead: true,
    displayColumnDefOptions: {
      "mrt-row-actions": { grow: 0, maxSize: 80, minSize: 80, size: 80 },
      /**
       * The expander holds one 28px chevron — the same size control as the checkbox beside it — so it
       * is given the same 40px the checkbox gets. Left alone, MRT sizes this column at 100px, which is
       * its default for an expander over sub-rows with no detail panel; in a panel that opens at its
       * 375px minimum that is 27% of the panel spent on a chevron, and it comes out of `Name`, the one
       * column with something to read in it.
       */
      "mrt-row-expand": { grow: 0, maxSize: 40, minSize: 40, size: 40 },
      "mrt-row-select": { grow: 0, maxSize: 40, minSize: 40, size: 40 },
    },
    initialState: {
      density: "compact",
      showGlobalFilter: true,
    },
    layoutMode: "grid",
    positionActionsColumn: "last",
    positionToolbarAlertBanner: "none",
    // The rows handed in are already the right ones: a panel backed by a paged query filtered and
    // paged them on the server, one backed by a list in hand did it in hand. Either way the table is
    // the renderer here, not the thing deciding what to show.
    manualFiltering: true,
    manualPagination: true,
    // Sorting is the exception, and only for a panel that asked for it — see `sorting`.
    manualSorting: Boolean(sorting),
    paginationDisplayMode: "pages",
    muiPaginationProps: {
      color: "primary",
      shape: "rounded",
      showRowsPerPage: false,
      siblingCount: 0,
      size: "small",
      variant: "outlined",
    },
    muiTopToolbarProps: {
      sx: {
        containerType: "inline-size",
        "& > .MuiBox-root": {
          "@container (min-width: 800px)": {
            alignItems: "flex-start",
            flexDirection: "row",
            paddingLeft: 3,
          },
          boxSizing: "border-box",
          gap: 1,
          justifyContent: "space-between",
          mt: 1,
          flexDirection: "column-reverse",
          paddingLeft: 0,
          paddingTop: 0,
          width: "100%",
          "& > .MuiBox-root": {
            flex: "0 1 auto",
            minWidth: 0,
          },
          "& > :first-child": {
            "@container (min-width: 800px)": {
              flex: "1 1 50%",
              pl: 0,
              width: "auto",
            },
            boxSizing: "border-box",
            pl: { xs: 0, md: 1 },
          },
          "& > .MuiBox-root:last-of-type": {
            "@container (min-width: 800px)": {
              flex: "1 1 50%",
              justifyContent: "flex-end",
            },
            alignItems: "center",
            boxSizing: "border-box",
            flex: 1,
            gap: 1,
            justifyContent: "space-between",
            minWidth: 0,
            pl: { xs: 0, md: 1 },
            width: "100%",
          },
        },
        width: "100%",
      },
    },
    muiBottomToolbarProps: {
      sx: {
        bottom: 0,
        flexShrink: 0,
        mt: "auto",
        position: "sticky",
      },
    },
    muiTableContainerProps: {
      sx: {
        flex: { xs: "0 0 auto", md: 1 },
        minHeight: 0,
        overflowX: "hidden",
      },
    },
    muiTableHeadCellProps: {
      sx: {
        px: { xs: 0.5, md: 1 },
        "& .Mui-TableHeadCell-Content": { minWidth: 0 },
      },
    },
    muiTableBodyCellProps: {
      sx: {
        minWidth: 0,
        px: { xs: 0.5, md: 1 },
      },
    },
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        display: "flex",
        flexDirection: "column",
        height: { xs: "auto", md: "100%" },
        width: "100%",
      },
    },
    muiSearchTextFieldProps: {
      placeholder: "Search",
      // No `slots.root` here on purpose. `MRTNoMemo` fills that slot with `MrtTextFieldRoot` — a
      // `FormControl` that drops the legacy `inputProps`/`InputProps` MRT still passes — and naming
      // a plain `FormControl` instead, which the document selector used to, put `inputProps` onto a
      // DOM node and made React complain about it on every render of the panel.
      sx: {
        flex: { xs: "1 1 auto", md: "0 1 auto" },
        minWidth: 0,
        width: { xs: "100%", md: "auto" },
      },
      slotProps: {
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: search.value ? (
            <InputAdornment position="end">
              <IconButton aria-label="Clear search" size="small" onClick={() => search.onChange("")}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      },
    },
    // No `muiToolbarAlertBannerProps` and no `showAlertBanner`. Both were set here, and both were
    // dead: `positionToolbarAlertBanner` is "none" a few lines up, so MRT draws no banner at all.
    // The four words they carried — "Error loading data" — are the ones #5235 reports a refused
    // reviewer seeing, and nobody has ever seen them from this panel. What a failing panel
    // actually shows is `renderEmptyRowsFallback` below, which is where the failure is named.
    //
    // The position stays "none". That banner is also where MRT reports how many rows are selected,
    // and selecting rows is the whole point of a template picker: turning it on for the sake of an
    // error would put a running "1 of 12 rows selected" above every panel in the app.
    muiSelectCheckboxProps,
    muiTableBodyRowProps,
    renderEmptyRowsFallback: emptyRowsFallback<TemplateSelectorRow>({
      errorDescription: status?.errorMessage,
      isError: status?.isError,
      isLoading: status?.isFetching,
      onRetry: status?.onRetry,
      subject,
    }),
    renderRowActions,
    renderToolbarInternalActions,
    renderTopToolbarCustomActions,
    onGlobalFilterChange: (updater) =>
      search.onChange(typeof updater === "function" ? String(updater(search.value) ?? "") : String(updater ?? "")),
    onPaginationChange: (updater) =>
      pagination.onChange(typeof updater === "function" ? updater(pagination.value) : updater),
    onRowSelectionChange: handleRowSelectionChange,
    // Offered only to a panel that asked for the sort. A table handed an `onSortingChange` takes it
    // to mean the sort is somebody else's to keep and stops applying one of its own — so passing it
    // unconditionally left every panel with a header that reported a press and never re-ordered.
    ...(sorting
      ? {
          onSortingChange: (updater: SortingState | ((previous: SortingState) => SortingState)) =>
            sorting.onChange(typeof updater === "function" ? updater(sorting.value) : updater),
        }
      : {}),
    rowCount: pagination.rowCount,
    state: {
      globalFilter: search.value,
      isLoading: status?.isLoading,
      pagination: pagination.value,
      rowSelection,
      showProgressBars: Boolean(status?.isFetching) && !status?.isLoading,
      ...(sorting ? { sorting: sorting.value } : {}),
    },
  };

  return (
    <TemplateAccordion
      className={className}
      compact={isCompact}
      elevation={0}
      expanded={expanded}
      onChange={(_event, isExpanded) => {
        if (isCompact) setPanelOpen(isExpanded);
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon color="primary" />}>
        <Typography component="span" color="primary">
          {selectedCount > 0 ? `${selectedCount} Selected` : collapsedLabel}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {children}
        <MRTNoMemo<TemplateSelectorRow> options={tableOptions} />
      </AccordionDetails>
    </TemplateAccordion>
  );
}

/**
 * The tint on a row that something else put into play — a template a selection came from, a person
 * already on the team. It is the green the document selector has used for that since it was
 * written, kept rather than re-picked so the three panels agree on what the colour means.
 */
const CONTRIBUTED_ROW_BACKGROUND = "rgba(0, 200, 0, 0.1)";

function renderNameCell({ row }: Readonly<{ row: MRT_Row<TemplateSelectorRow> }>) {
  const { content, name } = row.original;
  return <NameContainer>{content ?? <RowName>{name}</RowName>}</NameContainer>;
}
